import { push } from 'react-router-redux';
import { captureNetworkException } from '../../global/error_handler';
import redirectToLogin from './redirectToLogin';
import { REQUEST_OUTCOMES, requestTransport } from './requestTransport';
import { sameSearch } from '../../util/recordViewerHistory';

const dispatchLifecycleAction = (dispatch, lifecycleAction, payload, meta) => {
  dispatch({
    type: lifecycleAction,
    ...(payload === undefined ? {} : { payload }),
    ...(meta === undefined ? {} : { meta }),
  });
};

// A list fetch writes its query into the URL. Skip the write when the URL already shows
// that query: re-requesting the current list (a realtime or post-action refresh) would
// otherwise stack identical history entries, and browser Back would appear to do nothing
// (ENG-3550).
const isCurrentLocation = (query) => {
  if (typeof window === 'undefined' || !window.location) return false;
  let target;
  try {
    target = new URL(query, window.location.href);
  } catch {
    return false;
  }
  return target.pathname === window.location.pathname
    && sameSearch(target.search, window.location.search);
};

const dispatchRequestStart = (request, dispatch) => {
  if (request.query && !isCurrentLocation(request.query)) {
    dispatch(push(request.query));
  }

  dispatchLifecycleAction(dispatch, request.dispatch.begin, undefined, request.dispatch.meta);
};

const requestHasOwnership = (request) => Boolean(request.dispatch.meta?.requestOwnership);

const crud = (request) => async (dispatch) => {
  dispatchRequestStart(request, dispatch);

  const result = await requestTransport({
    method: request.method,
    url: request.url,
    data: request.data ? request.data : null,
    params: request.params,
    signal: request.signal,
    responseType: request.responseType,
  }, {
    // Opt-in per request: a POST that can commit server-side before its
    // response is rendered must not be replayed by the 503 retry loop.
    retryOnUnavailable: request.retryOnUnavailable !== false,
    onRetry: (retries) => {
      dispatch({
        type: 'RETRYING_REQUEST',
        payload: { retries },
      });
      // A normal viewer BEGIN changes the active owner. Replaying it after a
      // transport retry could let an old request reclaim ownership after the
      // user has already opened another record. Legacy callers retain their
      // historical repeated-BEGIN behavior.
      if (!requestHasOwnership(request)) dispatchRequestStart(request, dispatch);
    },
    onRetryFailed: () => dispatch({ type: 'RETRYING_FAILED' }),
    onUnauthorized: request.ignoreUnauthorized
      ? undefined
      : (error) => redirectToLogin(dispatch, error),
    onFailure: (error) => {
      if (error.response && error.response.status === 500) {
        captureNetworkException(error, {
          source: 'crud',
          endpoint: request.url,
          method: request.method,
        });
      } else if (!error.response) {
        captureNetworkException(error, {
          source: 'crud',
          endpoint: request.url,
          method: request.method,
        });
      }
    },
  });

  if (result.outcome === REQUEST_OUTCOMES.cancelled) {
    // Keep legacy cancellation actionless. Owned viewer requests need a keyed
    // terminal so the request that is still current can leave loading state;
    // stale terminals are rejected by currentRecordRequest.
    if (requestHasOwnership(request)) {
      dispatchLifecycleAction(
        dispatch,
        request.dispatch.fail,
        {},
        { ...request.dispatch.meta, cancelled: true },
      );
    }
    return { canceled: true };
  }

  if (result.outcome === REQUEST_OUTCOMES.failure) {
    dispatchLifecycleAction(
      dispatch,
      request.dispatch.fail,
      result.value,
      request.dispatch.meta,
    );
    return { ...result.value, error: true };
  }

  const response = result.value;
  dispatchLifecycleAction(
    dispatch,
    request.dispatch.end,
    response,
    request.dispatch.meta,
  );

  if (request.redirect) {
    dispatch(push(request.redirect));
  }

  if (request.callback) dispatch(request.callback(response));
  if (request.onSuccess) request.onSuccess(response);

  return response;
};

export default crud;
